/**
 * RelationTree Component — v2 with all fixes
 * Renders a single franchise's relation tree with visual connections.
 * User anime = green cards, Pending anime = standard cards with actions.
 * 
 * Fixes applied:
 * #1 - Clickable titles (AniList link) + click card for detail overlay
 * #2 - Title language respects settings (getDisplayTitle)
 * #3 - Green cards show user status (Planning/Watching/etc)
 * #5 - Default collapsed, controlled by parent
 * #7 - Bigger cards matching grouped view size
 * #9 - Double-click to select card
 * #10 - Orphaned nodes get franchise label
 */

import React, { useMemo, useRef } from 'react';
import { buildRelationTree, flattenTree } from '../services/relationTreeBuilder';
import { getRelationTypeDisplay, formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import './RelationTree.css';

// Relation type color mapping
const RELATION_COLORS = {
    'SEQUEL': '#ff9800',
    'PREQUEL': '#ff9800',
    'SIDE_STORY': '#9c27b0',
    'SPIN_OFF': '#9c27b0',
    'ALTERNATIVE': '#2196f3',
    'PARENT': '#4caf50',
    'SOURCE': '#00bcd4',
    'ADAPTATION': '#00bcd4',
    'RELATED': '#607d8b'
};

const USER_STATUS_MAP = {
    'CURRENT': { label: 'Watching', emoji: '▶️', color: '#4fc3f7' },
    'PLANNING': { label: 'Planning', emoji: '📅', color: '#ffb74d' },
    'COMPLETED': { label: 'Completed', emoji: '✅', color: '#81c784' },
    'DROPPED': { label: 'Dropped', emoji: '❌', color: '#ef5350' },
    'PAUSED': { label: 'On Hold', emoji: '⏸️', color: '#ce93d8' },
    'REPEATING': { label: 'Rewatching', emoji: '🔁', color: '#4db6ac' }
};

const ANIME_STATUS_MAP = {
    'FINISHED': 'Finished',
    'RELEASING': 'Airing',
    'NOT_YET_RELEASED': 'Upcoming',
    'CANCELLED': 'Cancelled',
    'HIATUS': 'Hiatus'
};

const RelationTree = ({
    group,
    relationsData,
    titlePreference,
    selectedAnime,
    onToggleSelect,
    onAddAnime,
    onRemoveAnime,
    onDetailClick,
    isAdding,
    addStatus,
    isCollapsed,
    onToggleCollapse
}) => {
    // Fix #9: Delayed click ref for single/double-click disambiguation
    const clickTimerRef = useRef(null);

    // Build tree structure
    const tree = useMemo(() =>
        buildRelationTree(group, relationsData, titlePreference),
        [group, relationsData, titlePreference]
    );

    // Flatten for rendering
    const flatNodes = useMemo(() => flattenTree(tree), [tree]);

    if (flatNodes.length === 0) return null;

    // Fix #2: Use getDisplayTitle for consistent title language
    const getTitle = (node) => {
        // For nodes with title object { romaji, english, native }
        if (node.title && typeof node.title === 'object' && (node.title.romaji || node.title.english)) {
            return getDisplayTitle({ title: node.title }, titlePreference);
        }
        // For nodes where title is already a resolved string
        if (typeof node.title === 'string') return node.title;
        return 'Unknown';
    };

    const getCoverUrl = (node) => {
        if (typeof node.coverImage === 'string') return node.coverImage;
        return node.coverImage?.large || node.coverImage?.medium || null;
    };

    const getReleaseDate = (node) => {
        if (!node.startDate) return null;
        const { year, month, day } = node.startDate;
        if (!year) return null;
        return formatReleaseDate({ year, month, day });
    };

    // Show per-episode duration from AniList (in minutes)
    const getDuration = (node) => {
        if (!node.duration) return null;
        return `${node.duration} min/ep`;
    };

    const pendingCount = tree.stats.pendingNodes;
    const allPendingIds = flatNodes
        .filter(item => !item.node.isInUserList)
        .map(item => item.node.id);
    const allSelected = allPendingIds.length > 0 && allPendingIds.every(id => selectedAnime.has(id));

    const toggleSelectAll = () => {
        if (allSelected) {
            allPendingIds.forEach(id => onToggleSelect(id, false));
        } else {
            allPendingIds.forEach(id => onToggleSelect(id, true));
        }
    };

    return (
        <div className="relation-tree-group">
            {/* Group Header — Fix #8: sticky header */}
            <div className="relation-tree-header" onClick={() => onToggleCollapse(group.id)}>
                <div className="relation-tree-header-left">
                    <span className={`relation-tree-chevron ${!isCollapsed ? 'expanded' : ''}`}>▶</span>
                    <h3 className="relation-tree-title">{group.name}</h3>
                    <span className="relation-tree-count">{pendingCount} pending</span>
                </div>
                <div className="relation-tree-header-right">
                    <label className="relation-tree-select-all" onClick={e => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        Select All
                    </label>
                </div>
            </div>

            {/* Tree Body */}
            {!isCollapsed && (
                <div className="relation-tree-body">
                    {flatNodes.map((item, index) => {
                        const { node, depth, relationType } = item;
                        const isUserAnime = node.isInUserList;
                        const isSelected = !isUserAnime && selectedAnime.has(node.id);
                        const title = getTitle(node);
                        const coverUrl = getCoverUrl(node);
                        const releaseDate = getReleaseDate(node);
                        const duration = getDuration(node);
                        const relationColor = RELATION_COLORS[relationType] || RELATION_COLORS['RELATED'];
                        const userStatus = isUserAnime ? USER_STATUS_MAP[node.userStatus] : null;

                        return (
                            <div
                                key={node.id}
                                className={`relation-tree-node ${isUserAnime ? 'user-node' : 'pending-node'} ${isSelected ? 'selected' : ''}`}
                                style={{ '--depth': depth, '--relation-color': relationColor }}
                            >
                                {/* Connection line and relation label */}
                                {depth > 0 && (
                                    <div className="relation-tree-connector">
                                        <div className="relation-tree-line" />
                                        <span
                                            className="relation-tree-label"
                                            style={{ backgroundColor: relationColor }}
                                        >
                                            {getRelationTypeDisplay(relationType)}
                                        </span>
                                    </div>
                                )}

                                {/* Fix #10: Orphaned node label */}
                                {depth === 0 && !isUserAnime && node.relatedTo && (
                                    <div className="relation-tree-orphan-label">
                                        Related to <strong>{node.relatedTo.title}</strong> ({getRelationTypeDisplay(node.relationType)})
                                    </div>
                                )}

                                {/* Card — Fix #1: onClick for detail, #9: onDoubleClick for select */}
                                <div
                                    className={`relation-tree-card ${isUserAnime ? 'user-card' : 'pending-card'}`}
                                    onClick={() => {
                                        // Delayed click: wait to see if double-click follows
                                        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
                                        clickTimerRef.current = setTimeout(() => {
                                            onDetailClick && onDetailClick(node);
                                            clickTimerRef.current = null;
                                        }, 250);
                                    }}
                                    onDoubleClick={(e) => {
                                        e.preventDefault();
                                        // Cancel the pending single-click (overlay)
                                        if (clickTimerRef.current) {
                                            clearTimeout(clickTimerRef.current);
                                            clickTimerRef.current = null;
                                        }
                                        // Toggle selection (pending only)
                                        if (!isUserAnime) {
                                            onToggleSelect(node.id, !isSelected);
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Selection checkbox (pending only) */}
                                    {!isUserAnime && (
                                        <label className="relation-tree-checkbox" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => onToggleSelect(node.id, !isSelected)}
                                            />
                                        </label>
                                    )}

                                    {/* Cover Image */}
                                    {coverUrl && (
                                        <div className="relation-tree-cover">
                                            <img src={coverUrl} alt={title} loading="lazy" />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="relation-tree-info">
                                        {/* Fix #1: Clickable title link */}
                                        <h3 className="relation-tree-name">
                                            <a
                                                href={`https://anilist.co/anime/${node.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {title}
                                            </a>
                                        </h3>

                                        <div className="relation-tree-meta">
                                            {node.format && (
                                                <span className="relation-tree-badge format">{node.format}</span>
                                            )}
                                            {node.episodes && (
                                                <span className="relation-tree-badge">{node.episodes} eps</span>
                                            )}
                                            {node.averageScore && (
                                                <span className="relation-tree-badge score">★ {(node.averageScore / 10).toFixed(1)}</span>
                                            )}
                                            {node.status && (
                                                <span className={`relation-tree-badge status-${node.status?.toLowerCase()}`}>
                                                    {ANIME_STATUS_MAP[node.status] || node.status}
                                                </span>
                                            )}
                                            {releaseDate && (
                                                <span className="relation-tree-badge">📅 {releaseDate}</span>
                                            )}
                                            {duration && (
                                                <span className="relation-tree-badge time">⏱️ {duration}</span>
                                            )}
                                        </div>

                                        {/* Fix #3: User status badge with list type */}
                                        {isUserAnime && userStatus && (
                                            <span
                                                className="relation-tree-user-status"
                                                style={{ color: userStatus.color }}
                                            >
                                                {userStatus.emoji} {userStatus.label}
                                            </span>
                                        )}

                                        {/* Description (truncated, pending only) */}
                                        {!isUserAnime && node.description && (
                                            <p className="relation-tree-description">
                                                {node.description.replace(/<[^>]*>/g, '').substring(0, 120)}...
                                            </p>
                                        )}

                                        {/* Action buttons (pending only) */}
                                        {!isUserAnime && (
                                            <div className="relation-tree-actions" onClick={e => e.stopPropagation()}>
                                                <button
                                                    className="relation-tree-btn add"
                                                    onClick={() => onAddAnime(node.id, addStatus)}
                                                    disabled={isAdding}
                                                    title="Add to List"
                                                >
                                                    {isAdding ? '⏳ Adding...' : '➕ Add to List'}
                                                </button>
                                                <button
                                                    className="relation-tree-btn remove"
                                                    onClick={() => onRemoveAnime(node.id)}
                                                    title="Remove from suggestions"
                                                >
                                                    🗑️ Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RelationTree;
